import pb, { pocketbaseBaseUrl } from './pocketbase';

// Telemetry is privacy-sensitive. We ship it as:
// - Disabled by default (no opt-in set)
// - Always gated by an explicit build-time flag
const TELEMETRY_ENABLED_ENV = process.env.REACT_APP_ENABLE_TELEMETRY === 'true';
const TELEMETRY_OPT_IN_FLAG = 'thryveloop_telemetry_opt_in';
const SNAPSHOT_ALERT_TYPES = [
  'pb_sync_snapshot_cache',
  'pb_sync_snapshot_unavailable'
];
const ALERT_EVENT_TYPES = new Set([
  'coach_context_build',
  'reminder_worker_slow_snapshot',
  'lifecycle_modal_load',
  'offline_sync_failure_rate_breach',
  'sync_slo_breach',
  'error_boundary_hit',
  ...SNAPSHOT_ALERT_TYPES
]);
const TELEMETRY_QUEUE_KEY = 'thryveloop_telemetry_queue';
const MAX_QUEUE_LENGTH = 50;
const MAX_TELEMETRY_STRING_LENGTH = 240;
const BLOCKED_TELEMETRY_KEYS = new Set([
  'authorization',
  'body',
  'content',
  'conversation',
  'messages',
  'password',
  'prompt',
  'secret',
  'stack',
  'token',
  'transcript'
]);
const BLOCKED_TELEMETRY_KEY_TERMS = [
  'apikey',
  'authorization',
  'body',
  'content',
  'conversation',
  'messages',
  'password',
  'privatekey',
  'prompt',
  'secret',
  'stack',
  'token',
  'transcript'
];
let flushingQueue = false;

const isBlockedTelemetryKey = (key) => {
  const lowerKey = (key || '').toString().toLowerCase();
  const compactKey = lowerKey.replace(/[^a-z0-9]/g, '');
  return (
    BLOCKED_TELEMETRY_KEYS.has(lowerKey) ||
    BLOCKED_TELEMETRY_KEY_TERMS.some((term) => compactKey.includes(term))
  );
};

const sanitizeTelemetryPayload = (value, depth = 0) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return value.slice(0, MAX_TELEMETRY_STRING_LENGTH);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    if (depth >= 2) return { count: value.length };
    return value.slice(0, 20).map((item) => sanitizeTelemetryPayload(item, depth + 1));
  }
  if (typeof value !== 'object') return String(value).slice(0, MAX_TELEMETRY_STRING_LENGTH);
  if (depth >= 3) return '[object]';

  const out = {};
  Object.entries(value).forEach(([rawKey, rawValue]) => {
    const key = (rawKey || '').toString();
    if (!key || isBlockedTelemetryKey(key)) {
      return;
    }
    out[key] = sanitizeTelemetryPayload(rawValue, depth + 1);
  });
  return out;
};

const shouldLogTelemetryAlert = (eventType, payload = {}) => {
  const normalizedType = (eventType || '').toString().trim();
  if (!ALERT_EVENT_TYPES.has(normalizedType)) return false;
  const durationMs = Number(payload?.durationMs ?? payload?.duration ?? payload?.elapsedMs ?? 0);
  if (normalizedType === 'coach_context_build') {
    return payload?.slow === true || durationMs >= 800;
  }
  if (normalizedType === 'lifecycle_modal_load') {
    return durationMs >= 1200;
  }
  return normalizedType !== 'pb_sync_snapshot_live';
};

const getTelemetryAlertLevel = (eventType) => {
  if (eventType === 'sync_slo_breach' || eventType === 'offline_sync_failure_rate_breach') {
    return 'error';
  }
  if (eventType === 'coach_context_build' || eventType === 'lifecycle_modal_load' || eventType === 'error_boundary_hit') {
    return 'warning';
  }
  return 'info';
};

const readOptIn = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(TELEMETRY_OPT_IN_FLAG) === '1';
  } catch (_) {
    return false;
  }
};

let telemetryOptedIn = readOptIn();
let telemetryDisabled = !TELEMETRY_ENABLED_ENV || !telemetryOptedIn;

export const getTelemetryOptIn = () => telemetryOptedIn === true;

export const setTelemetryOptIn = (optIn) => {
  const next = Boolean(optIn);
  telemetryOptedIn = next;
  telemetryDisabled = !TELEMETRY_ENABLED_ENV || !telemetryOptedIn;
  if (typeof window === 'undefined') {
    return telemetryOptedIn;
  }
  try {
    if (telemetryOptedIn) {
      window.localStorage.setItem(TELEMETRY_OPT_IN_FLAG, '1');
    } else {
      window.localStorage.removeItem(TELEMETRY_OPT_IN_FLAG);
    }
  } catch (_) {}
  // Privacy: if disabled, drop any queued events.
  if (telemetryDisabled) {
    persistTelemetryQueue([]);
  }
  return telemetryOptedIn;
};

const isTelemetryEnabled = () => TELEMETRY_ENABLED_ENV && telemetryOptedIn && !telemetryDisabled;

const initTelemetryDisabled = () => {
  if (typeof window === 'undefined') {
    telemetryOptedIn = false;
    telemetryDisabled = !TELEMETRY_ENABLED_ENV;
    return;
  }
  try {
    telemetryOptedIn = readOptIn();
    telemetryDisabled = !TELEMETRY_ENABLED_ENV || !telemetryOptedIn;
    if (telemetryDisabled) {
      persistTelemetryQueue([]);
    }
  } catch (_) {
    telemetryOptedIn = false;
    telemetryDisabled = !TELEMETRY_ENABLED_ENV;
  }
};

if (typeof window !== 'undefined') {
  initTelemetryDisabled();
}

const TELEMETRY_ALERT_PATH = '/api/telemetry/alert';
const TELEMETRY_EVENT_PATH = '/api/telemetry/event';

const buildTelemetryUrl = (path) => {
  if (!path) return pocketbaseBaseUrl;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${pocketbaseBaseUrl}${path}`;
};

const encodeTelemetryPayloadHeader = (data) => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  try {
    return encodeURIComponent(JSON.stringify(data));
  } catch (_) {
    return '';
  }
};

const isTelemetryAuthReady = () => {
  return Boolean(pb.authStore?.isValid && pb.authStore?.token && pb.authStore?.model?.id);
};

const postTelemetry = async (path, payload = {}) => {
  const headers = resolveTelemetryHeaders();
  if (!headers.Authorization) {
    const authError = new Error('Telemetry auth not ready');
    authError.status = 401;
    throw authError;
  }
  try {
    const headerType = (payload?.type || payload?.eventType || '').toString().trim();
    if (headerType) {
      headers['X-Telemetry-Type'] = headerType;
    }
  } catch (_) {}
  try {
    const encodedPayload = encodeTelemetryPayloadHeader(
      payload?.payload && typeof payload.payload === 'object'
        ? sanitizeTelemetryPayload(payload.payload)
        : {}
    );
    if (encodedPayload) {
      headers['X-Telemetry-Payload'] = encodedPayload;
    }
  } catch (_) {}
  try {
    const timestampHeader = (payload?.timestamp || payload?.eventTimestamp || '').toString().trim();
    if (timestampHeader) {
      headers['X-Telemetry-Timestamp'] = timestampHeader;
    }
  } catch (_) {}
  const response = await fetch(buildTelemetryUrl(path), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    credentials: 'omit'
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    const requestError = new Error(message || `Telemetry request failed with status ${response.status}`);
    requestError.status = response.status;
    throw requestError;
  }
  return response.json().catch(() => ({}));
};

const logTelemetryAlert = async (eventType, payload = {}, level = 'warning') => {
  if (!isTelemetryEnabled()) {
    return;
  }
  const normalizedType = (eventType || '').toString().trim();
  if (!normalizedType || !isTelemetryAuthReady()) {
    return;
  }
  try {
    await postTelemetry(TELEMETRY_ALERT_PATH, {
      eventType: normalizedType,
      type: normalizedType,
      metadata: sanitizeTelemetryPayload(payload),
      level
    });
  } catch (error) {
    console.warn('[telemetry] Failed to log alert to system health logs:', error?.message || error);
  }
};

const readTelemetryQueue = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(TELEMETRY_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const persistTelemetryQueue = (queue) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const trimmed = Array.isArray(queue) ? queue.slice(-MAX_QUEUE_LENGTH) : [];
    if (!trimmed.length) {
      window.localStorage.removeItem(TELEMETRY_QUEUE_KEY);
      return;
    }
    if (!isTelemetryEnabled()) {
      return;
    }
    window.localStorage.setItem(TELEMETRY_QUEUE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.warn('[telemetry] Failed to persist telemetry queue:', error?.message || error);
  }
};

const resolveTelemetryHeaders = () => {
  const headers = {
    'Content-Type': 'application/json'
  };
  if (isTelemetryAuthReady()) {
    headers.Authorization = `Bearer ${pb.authStore.token}`;
  }
  return headers;
};

const normalizeEventPayload = (event) => {
  if (!event) return null;
  if (event.type) return event;
  if (event.eventType) {
    return { ...event, type: event.eventType };
  }
  return null;
};

const sendTelemetryEvent = async (event) => {
  if (!isTelemetryEnabled()) {
    return;
  }
  const normalized = normalizeEventPayload(event);
  if (!normalized || !normalized.type) {
    throw new Error('Invalid telemetry payload');
  }
  return postTelemetry(TELEMETRY_EVENT_PATH, normalized);
};

const flushTelemetryQueue = async () => {
  if (
    !isTelemetryEnabled() ||
    flushingQueue ||
    typeof window === 'undefined' ||
    !isTelemetryAuthReady()
  ) {
    return;
  }

  const queue = readTelemetryQueue();
  if (!queue.length) {
    return;
  }

  flushingQueue = true;
  const remaining = [];

  for (const queuedEvent of queue) {
    const normalizedEvent = normalizeEventPayload(queuedEvent);
    if (!normalizedEvent || !normalizedEvent.type) {
      continue;
    }
    try {
      const { failedAt, error: queuedError, eventType, ...cleanEvent } = normalizedEvent || {};
      await sendTelemetryEvent(cleanEvent);
    } catch (error) {
      console.warn('[telemetry] Failed to flush queued event, keeping it for later:', error?.message || error);
      remaining.push(queuedEvent);
      break;
    }
  }

  persistTelemetryQueue(remaining);
  flushingQueue = false;
};

export const recordTelemetryEvent = async (eventType, payload = {}) => {
  if (!isTelemetryEnabled()) {
    return;
  }
  const normalizedType = (eventType || '').toString().trim();
  if (!normalizedType) {
    console.warn('[telemetry] Skipping event with missing type');
    return;
  }

  const event = {
    type: normalizedType,
    eventType: normalizedType,
    payload: sanitizeTelemetryPayload(payload),
    userId: pb.authStore?.model?.id || null,
    timestamp: new Date().toISOString()
  };

  if (!isTelemetryAuthReady()) {
    if (typeof window !== 'undefined') {
      const queue = readTelemetryQueue();
      queue.push({ ...event, failedAt: new Date().toISOString(), error: 'auth_not_ready' });
      persistTelemetryQueue(queue);
    }
    return;
  }

  try {
    await sendTelemetryEvent(event);
    if (shouldLogTelemetryAlert(normalizedType, event.payload)) {
      await logTelemetryAlert(normalizedType, event.payload, getTelemetryAlertLevel(normalizedType));
    }
    await flushTelemetryQueue();
  } catch (error) {
    const statusCode = Number(error?.status || 0);
    const message = (error?.message || '').toLowerCase();
    const isAuthError =
      statusCode === 401 ||
      statusCode === 403 ||
      message.includes('not authenticated') ||
      message.includes('requires valid record authorization token') ||
      message.includes('telemetry auth not ready');
    if (!isAuthError) {
      console.warn('[telemetry] Failed to record event, falling back to console only:', error);
    }
    const shouldPersist =
      typeof window !== 'undefined' &&
      !message.includes('missing event type');
    if (shouldPersist) {
      const queue = readTelemetryQueue();
      queue.push({ ...event, failedAt: new Date().toISOString(), error: error?.message });
      persistTelemetryQueue(queue);
    }
  }
};

export const recordTelemetryError = (eventType, error) => {
  if (!isTelemetryEnabled()) {
    return;
  }
  return recordTelemetryEvent(eventType, {
    error: error?.message || error,
    stack: error?.stack || null
  });
};

const telemetryAuthListenerKey = '__thryveloopTelemetryAuthListenerAttached';
if (
  typeof window !== 'undefined' &&
  pb?.authStore?.onChange &&
  !globalThis[telemetryAuthListenerKey]
) {
  globalThis[telemetryAuthListenerKey] = true;
  pb.authStore.onChange(() => {
    if (isTelemetryAuthReady()) {
      flushTelemetryQueue().catch((error) => {
        console.warn('[telemetry] Failed to flush queue after auth change:', error?.message || error);
      });
    }
  });
}
